import { SearchLg } from '@untitled-ui/icons-react/build/cjs';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import Radio from 'src/components/Radio/Radio';
import TruncateTextCmp from 'src/components/TruncateText';
import { smoothScrollIntoView } from 'src/utils/CommonFunctions';

import { LIMITED_ACCESS_LIST } from './quoteDetailModal.const';
import { ILimitedAccessDelivery } from './quoteDetailModal.interface';

const LimitedAccessCard = ({ val, isSelected, cardRef, onChangeRadio }: { val: any; isSelected: boolean; cardRef: React.Ref<HTMLDivElement>; onChangeRadio: (e: any) => void }) => {
    const labelRef = useRef<HTMLLabelElement>(null);

    return (
        <div
            ref={cardRef}
            role="button"
            tabIndex={0}
            onClick={() => onChangeRadio({ target: { value: val.value } })}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChangeRadio({ target: { value: val.value } });
                }
            }}
            className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-[8px] cursor-pointer transition-colors ${
                isSelected ? 'border-primary bg-primary/5' : 'border-utilityGray200 hover:border-grayLight300'
            }`}
        >
            <TruncateTextCmp refName={labelRef} message={val.name} truncateTextParentClassName="flex-1 min-w-0">
                <label htmlFor={`service_${val.value}`} ref={labelRef} className="text-xs leading-[20px] text-textSecondary cursor-pointer font-medium truncate block">
                    {val.name}
                </label>
            </TruncateTextCmp>
            <Radio id={`service_${val.value}`} onChangeFunc={onChangeRadio} inputName={'limitedAccessType'} value={val.value} checked={isSelected} />
        </div>
    );
};

const LimitedAccessDeliveryModal: FC<ILimitedAccessDelivery> = ({ handleClose, handleLimitedAccess, defaultFormValues }) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const selectedItemRef = useRef<HTMLDivElement>(null);
    const [limitedAccessTypeList, setLimitedAccessTypeList] = useState<any>(LIMITED_ACCESS_LIST);
    const [limitedAccessSearch, setLimitedAccessSearch] = useState<string>('');
    const [selectedLimitedAccessType, setSelectedLimitedAccessType] = useState<string>(defaultFormValues?.limitedAccessType || '');
    const [limitedAccessOther, setLimitedAccessOther] = useState<string>(defaultFormValues?.limitedAccessOther || '');
    const [isDisabled, setIsDisabled] = useState<boolean>(true);

    useEffect(() => {
        const filterTypes = LIMITED_ACCESS_LIST.filter(
            (type: any) => type.name.toLowerCase().includes(limitedAccessSearch.toLowerCase()) || type.value.toLowerCase().includes(limitedAccessSearch.toLowerCase())
        );
        setLimitedAccessTypeList(filterTypes);
    }, [limitedAccessSearch]);

    useEffect(() => {
        if (selectedLimitedAccessType && selectedItemRef.current && scrollContainerRef.current) {
            smoothScrollIntoView(selectedItemRef.current, {
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [selectedLimitedAccessType, limitedAccessTypeList]);

    const handleChangeRadio = (e: any) => {
        setSelectedLimitedAccessType(e.target.value);
        setIsDisabled(e.target.value === 'other');
    };

    const handleSubmit = () => {
        const data = {
            limitedAccessOther,
            selectedLimitedAccessType,
        };
        handleLimitedAccess(data);
        handleClose();
    };

    const handleText = (type: string) => (e: any) => {
        if (type === 'search') {
            setLimitedAccessSearch(e.target.value);
        } else {
            if (e.target.value && e.target.value.trim() !== '') {
                setIsDisabled(false);
            } else {
                setIsDisabled(true);
            }
            setLimitedAccessOther(e.target.value);
        }
    };

    return (
        <CommonModal
            title={t('QuotingHub.QuotingDashboard.Limited_access_delivery')}
            modalClassName="flex flex-col"
            handleClose={handleClose}
            size={'max-w-[688px] h-[90vh]'}
            secondaryBtnText={t('Common.Cancel')}
            primaryBtnText={t('Common.Save_Changes')}
            primaryBtnOnClick={handleSubmit}
            secondaryBtnOnClick={handleClose}
            primaryBtnDisabled={isDisabled}
        >
            <div className="p-0 flex-1 flex flex-col overflow-y-auto">
                <div className="px-4 pt-4">
                    <InputText
                        inputName="searchCarrier"
                        placeholder={t('Common.Search')}
                        className="bg-white focus:bg-white pl-8 placeholder:text-gray500 shadow-sm font-normal search-input"
                        icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                        value={limitedAccessSearch}
                        isClearable
                        onChangeFunc={handleText('search')}
                        inputType="text"
                        parentClassName="w-full mb-4"
                    />
                </div>
                {limitedAccessTypeList.length > 0 ? (
                    <div ref={scrollContainerRef} className="grid grid-cols-2 gap-1.5 flex-1 overflow-y-auto custom-scrollbar-v2 content-start px-4 pb-4">
                        {limitedAccessTypeList.map((val: any, i: any) => {
                            const isSelected = selectedLimitedAccessType === val.value;

                            return <LimitedAccessCard key={`limited_${i}`} val={val} isSelected={isSelected} cardRef={isSelected ? selectedItemRef : null} onChangeRadio={handleChangeRadio} />;
                        })}
                        {selectedLimitedAccessType === 'other' && (
                            <div className="py-1 col-span-2">
                                <InputText
                                    inputName="limitedAccessOther"
                                    placeholder={t('QuotingHub.QuotingDashboard.Please_describe_the_access_type')}
                                    label=""
                                    value={limitedAccessOther}
                                    onChangeFunc={handleText('other')}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <NotFoundUI
                        containerClassName="!my-4 "
                        iconClassName="!hidden"
                        iconSecondaryClassName="!flex"
                        isEmptyBackground={true}
                        iconSecondary={<SearchLg className="w-5 h-5 text-primary" />}
                        title={t('QuotingHub.QuotingDashboard.No_Services_Found')}
                        desc={t('QuotingHub.QuotingDashboard.No_Services_Found_Desc')}
                    />
                )}
            </div>
        </CommonModal>
    );
};

export default LimitedAccessDeliveryModal;
